import { useUploadStore } from "../../store/uploadStore";
import { assertEditorImportOriginalContext, markEditorImportRuntimeDispatched } from "./editorImportAdmission";
import { sendToUnityWithResponse } from "../../components/Unityboards/UnityBridge";
import { getFinalDownloadUrl } from "../api/viewr";
import { getFreshToken } from "../api/base";
import type { ExtendedUploadItem } from "../../store/uploadStore";
import type { ViewRData } from "../../types/unity";
import type { UnitySpaceNavigationStatePayload } from "../../types/unity";
import {
  UNITY_SPACE_NAVIGATION_STATE_EVENT,
} from "./spaceNavigationCanonicalState";
import { abortSpaceSwitch, beginSpaceSwitch } from "./spaceSwitchState";
import { startSpaceNavigationTransaction } from "./spaceNavigationCoordinator";

const DEFAULT_VIEWr_RELOAD_TIMEOUT_MS = 45000;

const toObjectPath = (viewrGcsPath: string): string | null => {
  const trimmed = viewrGcsPath.trim();
  // The backend accepts an object key, regardless of whether the project
  // storage URI came from the legacy GCS path or the AWS S3 migration path.
  if (!/^(?:gs|s3):\/\/[^/]+\/.+/.test(trimmed)) {
    return null;
  }
  const withoutBucket = trimmed.replace(/^(?:gs|s3):\/\/[^/]+\//, "");
  return withoutBucket.length > 0 ? withoutBucket : null;
};

export const resolveEditorImportVseIndex = ({
  viewRData,
  targetSpaceIndex,
  item,
}: {
  viewRData: ViewRData | null | undefined;
  targetSpaceIndex: number;
  item?: Pick<ExtendedUploadItem, "editorImportVseIndex"> | null;
}): number => {
  if (
    typeof item?.editorImportVseIndex === "number" &&
    Number.isFinite(item.editorImportVseIndex) &&
    item.editorImportVseIndex >= 0
  ) {
    return Math.trunc(item.editorImportVseIndex);
  }

  const space = viewRData?.spaces?.[targetSpaceIndex];
  if (!space || !Array.isArray(space.vseList)) {
    return 0;
  }

  return Math.max(0, space.vseList.length - 1);
};

export const buildEditorImportViewrReloadTransactionId = (
  uploadItemId: string,
): string => `editor-import-reload:${uploadItemId}:${Date.now()}`;

export const resolveUpdatedViewrSignedUrl = async (
  viewrGcsPath: string,
  token: string | null = null,
): Promise<string> => {
  const authToken = token ?? (await getFreshToken());
  const objectPath = toObjectPath(viewrGcsPath);
  if (!objectPath) {
    throw new Error("viewr_gcs_path must be a gs:// or s3:// object URI.");
  }

  const response = (await getFinalDownloadUrl(objectPath, authToken)) as {
    url?: string;
    signed_url?: string;
  } | null;

  const signedUrl =
    typeof response?.signed_url === "string" && response.signed_url.trim()
      ? response.signed_url.trim()
      : typeof response?.url === "string" && response.url.trim()
        ? response.url.trim()
        : null;

  if (!signedUrl) {
    throw new Error("Failed to resolve signed URL for updated .viewr.");
  }

  return signedUrl;
};

function readUnityEventTransactionId(detail: unknown): string | null {
  if (!detail || typeof detail !== "object") {
    return null;
  }
  const transactionId = (detail as { transactionId?: unknown }).transactionId;
  return typeof transactionId === "string" && transactionId.trim()
    ? transactionId.trim()
    : null;
}

function readModelLoadCompleteStatus(detail: unknown): string | null {
  if (!detail || typeof detail !== "object" || !("status" in detail)) {
    return null;
  }
  const status = (detail as { status?: unknown }).status;
  return typeof status === "string" ? status : null;
}

export const waitForEditorImportViewrReloadReady = (
  transactionId: string,
  timeoutMs: number = DEFAULT_VIEWr_RELOAD_TIMEOUT_MS,
): Promise<void> =>
  new Promise((resolve, reject) => {
    let settled = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const finish = (error: Error | null) => {
      if (settled) return;
      settled = true;
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
      window.removeEventListener(
        UNITY_SPACE_NAVIGATION_STATE_EVENT,
        onNavigationState as EventListener,
      );
      window.removeEventListener(
        "unity:reloadModelReady",
        onReloadModelReady as EventListener,
      );
      window.removeEventListener(
        "unity:modelLoadComplete",
        onModelLoadComplete as EventListener,
      );
      if (error) reject(error);
      else resolve();
    };

    const onNavigationState = (event: Event) => {
      const detail = (event as CustomEvent<UnitySpaceNavigationStatePayload | undefined>)
        .detail;
      if (!detail?.transactionId || detail.transactionId !== transactionId) {
        return;
      }
      if (detail.phase === "failed" || detail.status === "failed") {
        finish(new Error(detail.reasonCode ?? "editor_import_viewr_reload_failed"));
        return;
      }
      if (detail.phase !== "viewr_data_ready" && detail.phase !== "runtime_restored") {
        return;
      }
      finish(null);
    };

    const onReloadModelReady = (event: Event) => {
      const detail = (event as CustomEvent<unknown>).detail;
      const eventTransactionId = readUnityEventTransactionId(detail);
      if (eventTransactionId && eventTransactionId !== transactionId) {
        return;
      }
      console.warn(
        "[EDITOR] editor import .viewr reload ready via unity:reloadModelReady fallback",
        { transactionId, eventTransactionId },
      );
      finish(null);
    };

    const onModelLoadComplete = (event: Event) => {
      const detail = (event as CustomEvent<unknown>).detail;
      const status = readModelLoadCompleteStatus(detail);
      if (status === "vse_load_complete") {
        return;
      }
      const eventTransactionId = readUnityEventTransactionId(detail);
      if (eventTransactionId && eventTransactionId !== transactionId) {
        return;
      }
      console.warn(
        "[EDITOR] editor import .viewr reload ready via unity:modelLoadComplete fallback",
        { transactionId, eventTransactionId, status },
      );
      finish(null);
    };

    timeoutId = setTimeout(() => {
      finish(new Error("editor_import_viewr_reload_timeout"));
    }, timeoutMs);

    window.addEventListener(
      UNITY_SPACE_NAVIGATION_STATE_EVENT,
      onNavigationState as EventListener,
    );
    window.addEventListener(
      "unity:reloadModelReady",
      onReloadModelReady as EventListener,
    );
    window.addEventListener(
      "unity:modelLoadComplete",
      onModelLoadComplete as EventListener,
    );
  });

export const reloadUpdatedViewrBeforeEditorImport = async ({
  uploadItemId,
  viewrGcsPath,
  targetSpaceIndex,
  jobId,
  deferCurrentSpaceVseLoad = false,
  transactionId = buildEditorImportViewrReloadTransactionId(uploadItemId),
}: {
  uploadItemId: string;
  viewrGcsPath: string;
  targetSpaceIndex: number;
  jobId?: string | null;
  deferCurrentSpaceVseLoad?: boolean;
  transactionId?: string;
}): Promise<string> => {
  const executionId = useUploadStore.getState().items[uploadItemId]?.editorImportAdmission?.executionId;
  const guard = () => { if (executionId) assertEditorImportOriginalContext(uploadItemId, executionId); };
  guard();
  const signedViewrUrl = await resolveUpdatedViewrSignedUrl(viewrGcsPath);
  guard();
  const viewrFileName =
    viewrGcsPath.split("/").filter(Boolean).pop() || "project.viewr";

  // 이 reload가 metadata restore·direct VSE load·UI terminal을 끝까지 소유한다.
  // 등록하지 않으면 coordinator의 모든 진입점이 transaction 불일치로 no-op이 되어
  // canonical `completed`가 와도 overlay가 종료되지 않는다.
  const ownedSpaceIndex = targetSpaceIndex >= 0 ? targetSpaceIndex : null;
  beginSpaceSwitch(ownedSpaceIndex, "project_load", transactionId);
  startSpaceNavigationTransaction({
    transactionId,
    transactionType: "project_load",
    targetSpaceIndex: ownedSpaceIndex,
    source: "editorImportViewrReload",
  });

  const reloadReadyPromise = waitForEditorImportViewrReloadReady(transactionId);

  try {
    await sendToUnityWithResponse(
      "reactToUnity_processSignedUrlFile",
      {
        vseFileInfo: {
          fileName: viewrFileName,
          vsePublicUrl: signedViewrUrl,
          vseGCSUrl: viewrGcsPath,
          description: "",
        },
        finalViewRGcsPath: viewrGcsPath,
        targetSpaceIndex,
        importToCurrentSpace: targetSpaceIndex < 0,
        deferCurrentSpaceVseLoad,
        ...(executionId ? { editorImportExecution: { ownerId: uploadItemId, executionId } } : {}),
        transactionId,
        fromSSE: true,
        jobId: jobId ?? undefined,
      },
      { skipStaleTransactionCheck: true, assertCurrent: () => {
        guard();
        if (executionId && !deferCurrentSpaceVseLoad) markEditorImportRuntimeDispatched(uploadItemId, executionId);
      } },
    );

    await reloadReadyPromise;
    guard();
  } catch (error) {
    // 소유한 transaction을 남겨두면 이후 Space 전환이 stale 소유권에 막힌다.
    abortSpaceSwitch(transactionId, {
      source: "editorImportViewrReload",
      reason: "reload_failed",
    });
    throw error;
  }
  return transactionId;
};
